import type { IDisposable } from './IDisposable';

export class CallbackSubscriptionError extends Error {
  public constructor(m: string) {
    super(m);
    Object.setPrototypeOf(this, CallbackSubscriptionError.prototype);
  }
}

export class CallbackManagerError extends Error {
  public constructor(m: string) {
    super(m);
    Object.setPrototypeOf(this, CallbackManagerError.prototype);
  }
}

export class CallbackSubscription implements IDisposable {
  private _unsubscribe?: (key: number) => void;

  private _id: number;

  public constructor(unsubscribe: (key: number) => void, id: number) {
    this._unsubscribe = unsubscribe;
    this._id = id;
  }

  public unsubscribe(): void {
    if (!this._unsubscribe) {
      throw new CallbackSubscriptionError('Already unsubscribed');
    }
    this._unsubscribe(this._id);
    this._unsubscribe = undefined;
  }

  // IDisposable
  public dispose(): void {
    this.unsubscribe();
  }

  // For Unit Testing
  protected __directUnsubscribe(): void {
    this._unsubscribe?.(this._id);
  }
}

export type __CallbackSubscriptionConstructor = (
  unsubscribe: (key: number) => void,
  id: number
) => CallbackSubscription; // For Unit Testing

export class CallbackManager<Functor> {
  private _callbackSubscriptionConstructor: __CallbackSubscriptionConstructor;

  private _functors: Map<number, Functor>;

  private _identity: number;

  public constructor() {
    this._callbackSubscriptionConstructor = (unsubscribe: (key: number) => void, id: number): CallbackSubscription => {
      return new CallbackSubscription(unsubscribe, id);
    };
    this._functors = new Map();
    this._identity = 0;
  }

  public subscribe(functor: Functor): CallbackSubscription {
    this._functors.set(this._identity, functor);
    return this._callbackSubscriptionConstructor((id: number) => {
      if (!this._functors.has(id)) {
        throw new CallbackManagerError('Already unsubscribed!');
      }
      this._functors.delete(id);
    }, this._identity++);
  }

  public invoke(...args: Array<unknown>): void {
    for (const [, value] of this._functors) {
      const callback = value as unknown as (...params: Array<unknown>) => unknown;
      callback(...args);
    }
  }

  public invokeAsync(...args: Array<unknown>): Promise<unknown> {
    const promises = [];
    for (const [, value] of this._functors) {
      const callback = value as unknown as (...params: Array<unknown>) => unknown;
      promises.push(callback(...args));
    }
    return Promise.all(promises);
  }

  // For Unit Testing
  public __setCallbackSubscriptionConstructor(
    callbackSubscriptionConstructor: __CallbackSubscriptionConstructor
  ): void {
    this._callbackSubscriptionConstructor = callbackSubscriptionConstructor;
  }
}
